import { useState } from "react";
import axios from "axios";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { WA_LINK } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3.5 text-sm text-cream placeholder:text-slate-500 outline-none transition-colors focus:border-saffron";

export default function EnquiryForm({ type, withInvestment = false, testid }) {
  const empty = { name: "", phone: "", email: "", city: "", investment: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Please add your name and phone number.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiry`, { type, ...form });
      toast.success(
        type === "franchisee"
          ? "Enquiry received! Our franchise team will call you soon."
          : "Message sent! We'll get back to you shortly."
      );
      setForm(empty);
    } catch {
      toast.error("Could not send right now — please WhatsApp us instead.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form data-testid={testid} onSubmit={submit} className="rounded-3xl border border-white/10 bg-coal p-7 sm:p-9">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input
          data-testid={`${testid}-name`}
          required
          value={form.name}
          onChange={set("name")}
          placeholder="Your name *"
          className={inputCls}
        />
        <input
          data-testid={`${testid}-phone`}
          required
          value={form.phone}
          onChange={set("phone")}
          placeholder="Phone / WhatsApp *"
          className={inputCls}
        />
        <input
          data-testid={`${testid}-email`}
          type="email"
          value={form.email}
          onChange={set("email")}
          placeholder="Email (optional)"
          className={withInvestment ? inputCls : `${inputCls} sm:col-span-2`}
        />
        {withInvestment && (
          <input
            data-testid={`${testid}-city`}
            value={form.city}
            onChange={set("city")}
            placeholder="City where you want to open"
            className={inputCls}
          />
        )}
        {withInvestment && (
          <select
            data-testid={`${testid}-investment`}
            value={form.investment}
            onChange={set("investment")}
            className={`${inputCls} sm:col-span-2 [&>option]:bg-coal ${form.investment ? "" : "text-slate-500"}`}
          >
            <option value="" disabled>
              Estimated investment budget
            </option>
            <option>Under ₹25 Lakh</option>
            <option>₹25 – 50 Lakh</option>
            <option>₹50 Lakh – 1 Crore</option>
            <option>₹1 Crore+</option>
          </select>
        )}
        <textarea
          data-testid={`${testid}-message`}
          rows={4}
          value={form.message}
          onChange={set("message")}
          placeholder={withInvestment ? "Tell us about yourself and your plans" : "How can we help?"}
          className={`${inputCls} resize-none sm:col-span-2`}
        />
      </div>
      <button
        data-testid={`${testid}-submit`}
        type="submit"
        disabled={loading}
        className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-saffron py-4 font-display text-base font-semibold text-obsidian transition-[transform,background-color] duration-300 hover:-translate-y-0.5 hover:bg-flame hover:text-cream disabled:opacity-60"
      >
        {loading ? <Loader2 size={18} className="animate-spin" /> : <Send size={17} />}
        {loading ? "Sending…" : "Send Enquiry"}
      </button>
      <p className="mt-4 text-center text-xs text-slate-500">
        Prefer talking?{" "}
        <a data-testid={`${testid}-whatsapp-alt`} href={WA_LINK} target="_blank" rel="noopener noreferrer" className="text-wa underline underline-offset-2">
          WhatsApp us directly
        </a>
      </p>
    </form>
  );
}
